import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Tooltip } from 'antd';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { faCircleExclamation, faPlus, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce, sortBy } from 'lodash-es';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { MSTeamsEntity, MsTeamsTargetingClient, RosterClient, TargetRespondent } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExSelect } from 'jf/components/DevExSelect';
import { MSTeamsEntityCard } from 'jf/components/MSTeamsEntityCard';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

type EmptyMSTeamsRosterDisplayProps = {
  user: TargetRespondent;
  rosterUsers: TargetRespondent[];
  onAdd: () => void;
};
export const EmptyMSTeamsRosterDisplay: React.FC<EmptyMSTeamsRosterDisplayProps> = ({
  user,
  rosterUsers,
  onAdd,
}) => {
  const theme = useDevExTheme();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [msteamsUser, setMsteamsUser] = useState();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const { data: msteamsEntities, isFetching } = useClientQuery(
    MsTeamsTargetingClient.getMsteamsEntities,
    { filter: debouncedSearchQuery },
    { enabled: debouncedSearchQuery.length >= 1 }
  );
  const { studyRef } = useParams<{ studyRef: string }>();

  const debounceFiredSearchQuery = useMemo(() => debounce(setDebouncedSearchQuery, 300), []);
  useEffect(() => {
    if (searchQuery) {
      debounceFiredSearchQuery(searchQuery);
    } else {
      debounceFiredSearchQuery.cancel();
      setDebouncedSearchQuery('');
    }
  }, [searchQuery]);

  const rosterEmail = rosterUsers?.map((user) => user.email);
  const msteamsUsers = msteamsEntities?.filter(
    (user) => user.type === MSTeamsEntity.type.USER && user.email !== null
  );

  const { mutateAsync: UpdateStudyRoster } = useClientMutation(RosterClient.updateStudyTargets);

  const isSearching = searchQuery !== debouncedSearchQuery || isFetching;
  const msteamsEntityOptions = useMemo(() => {
    return sortBy(msteamsUsers, (entity) => entity.name.toLowerCase()).map((entity) => {
      const disabled = entity.email && rosterEmail?.includes(entity.email) ? true : false;
      return {
        value: entity.id,
        key: '@' + entity.name,
        label: (
          <Tooltip
            title={
              disabled ? 'This MS Teams user is already linked to a person in your roster' : ''
            }
          >
            <MSTeamsEntityCard
              name={entity.name}
              displayName={entity.displayName}
              imageBytes={entity.image}
              disabled={disabled}
            />
          </Tooltip>
        ),
        disabled: disabled,
      };
    });
  }, [msteamsEntities]);

  return (
    <Flex gap={theme.variable.spacing.md} align="center">
      <Tooltip
        title={
          `${user.name}` +
          ' does not have Msteams linked to their profile. Link a Msteams profile here. '
        }
      >
        <FontAwesomeIcon
          icon={faCircleExclamation}
          style={{
            color: theme.color.status.warning.text,
            paddingInlineStart: theme.variable.spacing.sm,
          }}
        />
      </Tooltip>
      <Flex vertical gap={theme.variable.spacing.xs}>
        <div style={{ fontSize: theme.variable.fontSize.md }}> {user.name} </div>
        <div style={{ color: theme.color.text.secondary, minWidth: 200, wordBreak: 'break-all' }}>
          {user.email}
        </div>
      </Flex>
      <DevExSelect
        showSearch
        filterOption={() => true} // leave filtering to the BE only
        options={!isSearching ? msteamsEntityOptions : undefined}
        onChange={(value) => {
          UpdateStudyRoster({
            studyRef: studyRef!,
            targetRef: user?.publicReferenceId,
            requestBody: {
              name: user.name,
              email: user.email,
              msteams: {
                id: value,
              },
            },
          }).then(() => {
            trackEvent('survey-editor:msteams-target-editor:link-user', {
              studyRef: studyRef,
              targetRef: user?.publicReferenceId,
              id: value,
            });

            queryClient.invalidateQueries('GET_STUDY_TARGETS');
            setMsteamsUser(value);
          });
          setSearchQuery('');
        }}
        optionLabelProp="key"
        style={{ height: 40, width: 200 }}
        loading={isSearching}
        suffixIcon={<FontAwesomeIcon icon={faSearch} />}
        placeholder="Search for MS Teams user"
        notFoundContent={
          isSearching ? (
            <DevExLoader tip="Searching MS Teams..." />
          ) : (
            <DevExEmpty
              icon={faMicrosoft}
              label={
                searchQuery
                  ? `No MS Teams targets found matching "${searchQuery}".`
                  : 'Search for @users and @teams in your Microsoft Teams instance.'
              }
            />
          )
        }
        onSearch={setSearchQuery}
      />
      <DevExButton
        icon={<FontAwesomeIcon icon={faPlus} />}
        disabled={!msteamsUser}
        onClick={() => onAdd()}
      >
        Add
      </DevExButton>
    </Flex>
  );
};
